import { CombinedGraphQLErrors, ServerError } from '@apollo/client';
import { toDate, parseISO } from 'date-fns';
import type { InferGetServerSidePropsType, NextPage, Route } from 'next';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';

import { Headlines } from '#components/Headlines/Headlines';
import { HeadlinesDocument } from '#components/Headlines/__generated__/Headlines.generated';
import { NAVIGATION_ITEM_HEADLINES } from '#config/constants';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import { dropUndefined } from '#cutils/misc/drop-undefined';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import type { CacheTag } from '#cutils/page-context/edge-cache-tags';
import { createDefaultGetServerSideProps } from '#cutils/page-context/server-side-props';
import { buildContentPageSeoConfig } from '#cutils/seo/buildContentPageSeoConfig';
import { envConfigServer } from '#sconfig/env.server';
import { graphqlErrorPathsMatch, GraphQLSemanticError } from '#sutils/graphql-error-matcher';
import { logger } from '#utils/logging/logger';
import { useTracktor } from 'react-tracktor';

import { getHeadlinesArticleModuleType, mergeLists, type HeadlineItem } from '../client/components/Headlines/Headlines.utils';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const SEO_DESCRIPTION = 'Mit den Schlagzeilen von BR24 erhalten Sie alle Nachrichten des Tages auf einen Blick.';
const SEO_TITLE = 'Schlagzeilen: Alle Nachrichten von BR24. Für Sie auf einen Blick';

const Page: NextPage<Props> = ({ newsItems }) => {
	const { constants } = useEnvironment();
	useTracktor({ pageViewData: { data: NAVIGATION_ITEM_HEADLINES, event: { type: 'PageViewEvent' } } });

	const seoConfig = buildContentPageSeoConfig({ description: SEO_DESCRIPTION, title: SEO_TITLE, url: `${constants.appUrl}/schlagzeilen` });

	return (
		<>
			<Head>{generateNextSeo(seoConfig)}</Head>

			<Headlines items={newsItems} />
		</>
	);
};

const LATEST_ARTICLES_PATH = ['headlines'];
const SHORTNEWS_PATH = ['openmedia'];

export const getServerSideProps = createDefaultGetServerSideProps(async (_ctx, apolloClient, withCache) => {
	try {
		const { data, error } = await apolloClient.query({
			query: HeadlinesDocument,
		});

		if (CombinedGraphQLErrors.is(error)) {
			// only throw an error if both datasources contain errors

			const latestArticleError = error.errors.find((error) => error.path && graphqlErrorPathsMatch(error.path, LATEST_ARTICLES_PATH));
			const shortnewsError = error.errors.find((error) => error.path && graphqlErrorPathsMatch(error.path, SHORTNEWS_PATH));

			if (latestArticleError && shortnewsError) {
				throw new GraphQLSemanticError(
					`Semantic GraphQL errors in path '${latestArticleError.path?.join('.')}' and '${shortnewsError.path?.join('.')}' with error messages ${latestArticleError.message} and ${shortnewsError.message}`
				);
			}
		}

		if (ServerError.is(error)) {
			throw error;
		}

		const navigationTitle = NAVIGATION_ITEM_HEADLINES.pageTitle;

		const newsItems = mergeLists(data, envConfigServer.SELF_ORIGIN);

		const cacheTags = new Set<CacheTag>();
		if (withCache) {
			for (const item of newsItems) {
				if (item.__typename === 'Article') {
					cacheTags.add(`a.${item.rowId}`);
				} else if (item.__typename === 'ShortnewsItem') {
					cacheTags.add(`sn.${item.omId}`);
				}
			}
		}

		const currentDate = new Date();

		const items = newsItems
			.map((item) => {
				if (item.__typename === 'Article') {
					return {
						date: item.publicationDate,
						icon: getHeadlinesArticleModuleType(item, currentDate),
						path: item.path as Route,
						title: item.title,
						id: item.rowId,
					} satisfies HeadlineItem;
				} else if (item.__typename === 'ShortnewsItem') {
					const { shareUrl, title, lastEdited } = item;

					if (!shareUrl || !title) {
						return null;
					}

					const dateStr = lastEdited ? toDate(parseISO(lastEdited)).toString() : null;

					return {
						date: dateStr,
						icon: 'TEXT',
						path: shareUrl as Route,
						title,
						id: item.omId!,
					} satisfies HeadlineItem;
				} else {
					return undefined;
				}
			})
			.filter(notNullOrUndefined);

		return {
			props: { newsItems: dropUndefined(items), navigationTitle, cacheTags },
		};
	} catch (error: any) {
		logger.error(error, 'Error during resolution of schlagzeilen');
		throw error;
	}
});

export default Page;
